/**
 * Zentrale SEO-Daten dieser Website (Bali VW Tour).
 *
 * Alle Titel folgen dem Muster:  "<Keyword vorn> | Bali VW Tour"
 * und bleiben unter 60 Zeichen, damit Google sie vollständig anzeigt.
 * Beschreibungen liegen zwischen 120 und 160 Zeichen.
 */

/** In Umgebungsvariablen hinterlegte Produktions-Domain (falls vorhanden). */
const ENV_SITE_URL =
  typeof import.meta !== "undefined"
    ? (import.meta.env?.VITE_SITE_URL as string | undefined)
    : undefined;

/** Produktions-Domain der Website (immer ohne Schrägstrich am Ende). */
export const SITE_URL =
  ENV_SITE_URL && ENV_SITE_URL.trim()
    ? ENV_SITE_URL.trim().replace(/\/+$/, "")
    : "https://www.balivwtour.com";

export const SITE_NAME = "Bali VW Tour";

/** Standard-Vorschaubild für Social Media / Open Graph. */
export const DEFAULT_OG_IMAGE =
  "https://cskixmpibljkzhgcgnhi.supabase.co/storage/v1/object/public/site-images/villa-kompiang/pool_01.jpg";

/** Zentrale, wahrheitsgemäße Angaben zum Betrieb (lokales SEO). */
export const BUSINESS = {
  name: SITE_NAME,
  description:
    "Private Villen mit Pool in Mengwi, Bali – mit authentischen Touren, Tagesausflügen und Hochzeiten in den Reisfeldern.",
  street: "Jalan Ganetri 9 D, Gatot Subroto Timur",
  city: "Denpasar",
  region: "Bali",
  postalCode: "80235",
  country: "ID",
  phone: "+623618956561",
  email: "office@balivwtour.com",
  lat: -8.648,
  lng: 115.221,
  priceRange: "$$",
  sameAs: [
    "https://www.facebook.com/balivwtour",
    "https://instagram.com/balivwtour/",
    "https://twitter.com/balivwtour",
  ],
};

/** Offizieller Social-Media-Handle der Marke. */
export const TWITTER_HANDLE = "@balivwtour";

export interface SeoMeta {
  title: string;
  description: string;
}

export interface SeoEntry {
  de: SeoMeta;
  en: SeoMeta;
  /** Optionales, seitenspezifisches Vorschaubild (sonst DEFAULT_OG_IMAGE). */
  image?: string;
}

/**
 * Seiten, die NICHT in den Suchindex gehören:
 * Buchungsformulare und die persönliche Buchungsbestätigung.
 */
export const NOINDEX_ROUTES = [
  "/voucher",
  "/booking",
  "/booking-multiday",
  "/booking-rafting",
  "/booking-sailing",
];

/** Routen, die zwar existieren, aber nicht als eigene Seite beworben werden. */
export const NON_SITEMAP_ROUTES = ["/tour"];

export const defaultSeo: SeoEntry = {
  de: {
    title: "Villen, Touren & Hochzeiten auf Bali | Bali VW Tour",
    description:
      "Private Villen mit Pool, Touren im original VW Kübelwagen und Hochzeiten im tropischen Garten – alles persönlich betreut in Mengwi, Bali.",
  },
  en: {
    title: "Villas, Tours & Weddings in Bali | Bali VW Tour",
    description:
      "Private pool villas, tours in an original VW Kübelwagen and weddings in a tropical garden – all personally hosted by our family in Mengwi, Bali.",
  },
};

export const seoMap: Record<string, SeoEntry> = {
  "/": defaultSeo,
  "/villas": {
    de: {
      title: "Drei private Villen in den Reisfeldern | Bali VW Tour",
      description:
        "Villa Frangipani, Bougainvillea und Hibiskus: drei private Villen in Reisfeldlandschaft beim Taman-Ayun-Tempel – mit Komfort, Privatsphäre und persönlichem Service.",
    },
    en: {
      title: "Our Three Private Villas in the Rice Fields | Bali VW Tour",
      description:
        "Villa Frangipani, Bougainvillea and Hibiscus: three private villas in rice-field scenery near Taman Ayun Temple, with comfort, privacy and personal service.",
    },
  },
  "/villa-info": {
    de: {
      title: "Villa-Infos: Lage, Ausstattung & Service | Bali VW Tour",
      description:
        "Details zu Lage, Ausstattung und Service bei Bali VW Tour: 500 Meter vom UNESCO-Weltkulturerbe Taman Ayun in Mengwi, umgeben von grünen Reisfeldern.",
    },
    en: {
      title: "Villa Information: Location & Amenities | Bali VW Tour",
      description:
        "Details on location, amenities and service at Bali VW Tour: 500 metres from the UNESCO World Heritage site Taman Ayun in Mengwi, surrounded by rice fields.",
    },
  },
  "/villa-frangipani": {
    de: {
      title: "Villa Frangipani mit großer Terrasse | Bali VW Tour",
      description:
        "Villa Frangipani: zwei große Zimmer, ein großes Badezimmer und eine sehr große Terrasse – ideal für Paare oder Familien bis vier Personen. Ab 107 Euro pro Nacht.",
    },
    en: {
      title: "Villa Frangipani with Large Terrace | Bali VW Tour",
      description:
        "Villa Frangipani: two large rooms, one large bathroom and a very generous terrace – ideal for couples or families of up to four. From 115 US$ per night.",
    },
  },
  "/villa-bougainvillea": {
    de: {
      title: "Villa Bougainvillea – romantisch für Paare | Bali VW Tour",
      description:
        "Villa Bougainvillea: geräumiges Zimmer, großes Badezimmer und einladende Terrasse – die kleinste und romantischste unserer Villen. 107 Euro pro Nacht.",
    },
    en: {
      title: "Villa Bougainvillea – Romantic Hideaway | Bali VW Tour",
      description:
        "Villa Bougainvillea: spacious room, large bathroom and inviting terrace – the smallest and most romantic of our villas. 115 US$ per night.",
    },
  },
  "/villa-hibiscus": {
    de: {
      title: "Villa Hibiskus mit 11×6-m-Pool | Bali VW Tour",
      description:
        "Villa Hibiskus: zwei oder drei Zimmer mit eigenem Bad, Wohnzimmer, voll ausgestattete Küche, Speiseterrasse und ein 11×6 Meter großer Pool. Ab 214 Euro pro Nacht.",
    },
    en: {
      title: "Villa Hibiscus with 11×6 m Pool | Bali VW Tour",
      description:
        "Villa Hibiscus: two or three en-suite rooms, living room, fully equipped kitchen, dining terrace and an 11×6 metre pool. From 230 US$ per night in Mengwi.",
    },
  },
  "/villa-location": {
    de: {
      title: "Lage & Umgebung bei Taman Ayun, Mengwi | Bali VW Tour",
      description:
        "Entdecken Sie die Umgebung: Taman-Ayun-Tempel, lokaler Markt, Reisfelder und der Sangeh Monkey Forest liegen nur wenige Minuten von unseren Villen entfernt.",
    },
    en: {
      title: "Location & Surroundings near Taman Ayun | Bali VW Tour",
      description:
        "Discover the surroundings: Taman Ayun Temple, the local market, rice fields and Sangeh Monkey Forest are only a few minutes away from our private villas in Mengwi.",
    },
  },
  "/about": {
    de: {
      title: "25 Jahre Gastfreundschaft in Mengwi | Bali VW Tour",
      description:
        "Lernen Sie Team und Geschichte der Bali VW Tour kennen: über 25 Jahre Erfahrung mit privaten Villen, Touren und Hochzeiten in Mengwi auf Bali.",
    },
    en: {
      title: "25 Years of Hospitality in Mengwi | Bali VW Tour",
      description:
        "Meet the team and the story behind Bali VW Tour: more than 25 years of experience with private villas, tours and weddings in Mengwi on the island of Bali.",
    },
  },
  "/nostalgie": {
    de: {
      title: "Nostalgie – Presse, Fernsehen und Geschichte | Bali VW Tour",
      description:
        "Ein Blick zurück: Pressereisen mit Singapore Airlines, Ritz-Carlton und Four Seasons, Filmteams von n-tv, ORF und VOX TOURS – das Archiv der Bali VW Tour seit 1988.",
    },
    en: {
      title: "Nostalgia – Press, TV and History | Bali VW Tour",
      description:
        "A look back: press trips with Singapore Airlines, Ritz-Carlton and Four Seasons, film crews from n-tv, ORF and VOX TOURS – the Bali VW Tour archive since 1988.",
    },
  },
  "/your-stay": {
    de: {
      title: "Bali-Aufenthalt aus Bausteinen planen | Bali VW Tour",
      description:
        "Stellen Sie Ihren Bali-Aufenthalt aus flexiblen Bausteinen zusammen: Villa, Touren, Ausflüge und Extras – individuell geplant und persönlich betreut.",
    },
    en: {
      title: "Build Your Bali Stay from Modules | Bali VW Tour",
      description:
        "Compose your Bali stay from flexible modules: villa, tours, day trips and extras – individually planned and personally looked after from the first enquiry.",
    },
  },
  "/plan-your-stay": {
    de: {
      title: "Reise planen mit dem Baustein-Konfigurator | Bali VW Tour",
      description:
        "Planen Sie Ihren Aufenthalt mit dem Baustein-Konfigurator: Villa, Touren und Aktivitäten nach Ihren Wünschen kombinieren – in wenigen Schritten fertig.",
    },
    en: {
      title: "Plan Your Trip with the Configurator | Bali VW Tour",
      description:
        "Plan your stay with our configurator: combine villa, tours and activities exactly to your liking and put your personal Bali itinerary together in a few steps.",
    },
  },
  "/villa-reservation": {
    de: {
      title: "Villa anfragen und Verfügbarkeit prüfen | Bali VW Tour",
      description:
        "Wählen Sie Ihre Reisedaten und prüfen Sie die Verfügbarkeit unserer Villen in Mengwi. Wir antworten persönlich mit einem passenden Angebot für Ihren Aufenthalt.",
    },
    en: {
      title: "Villa Reservation & Availability | Bali VW Tour",
      description:
        "Choose your travel dates and check availability of our villas in Mengwi. We reply personally with a suitable offer for your stay in the rice fields of Bali.",
    },
  },
  "/booking": {
    de: {
      title: "Villa buchen: Verfügbarkeit prüfen | Bali VW Tour",
      description:
        "Wählen Sie Ihre Reisedaten im Kalender und prüfen Sie die Verfügbarkeit Ihrer privaten Villa auf Bali. Wir melden uns persönlich bei Ihnen zurück.",
    },
    en: {
      title: "Book Your Villa: Check Availability | Bali VW Tour",
      description:
        "Choose your travel dates in the calendar and check availability for your private villa in Bali. We will get back to you personally with all the details.",
    },
  },
  "/booking-multiday": {
    de: {
      title: "Bali-Rundreise mit privatem Guide buchen | Bali VW Tour",
      description:
        "Buchen Sie Ihre mehrtägige Bali-Rundreise mit privatem Guide: Datum und Teilnehmerzahl wählen, wir planen die Route und kümmern uns um alles Weitere.",
    },
    en: {
      title: "Book a Bali Round Trip with Private Guide | Bali VW Tour",
      description:
        "Book your multi-day Bali round trip with a private guide: choose your dates and the number of travellers, we plan the route and take care of everything else.",
    },
  },
  "/booking-rafting": {
    de: {
      title: "Rafting auf dem Ayung-Fluss buchen | Bali VW Tour",
      description:
        "Buchen Sie Ihre Rafting-Tour auf dem Ayung-Fluss: Ausrüstung, Transfer und Buffet inklusive – ein actionreicher Tag im grünen Herzen Balis.",
    },
    en: {
      title: "Book Ayung River Rafting | Bali VW Tour",
      description:
        "Book your rafting tour on the Ayung River: equipment, transfer and buffet included – an action-packed day in the green heart of Bali with our team.",
    },
  },
  "/booking-sailing": {
    de: {
      title: "Segeltörn nach Lembongan buchen | Bali VW Tour",
      description:
        "Buchen Sie Ihren Segeltörn zur Insel Lembongan: Katamaran, Schnorcheln, BBQ-Buffet und die Rückfahrt im Sonnenuntergang über der Bali-See.",
    },
    en: {
      title: "Book a Sailing Trip to Lembongan | Bali VW Tour",
      description:
        "Book your sailing trip to Lembongan Island: catamaran, snorkelling, BBQ buffet and a return sail into the sunset over the Bali Sea with our experienced crew.",
    },
  },
  "/weddings": {
    de: {
      title: "Heiraten im tropischen Garten auf Bali | Bali VW Tour",
      description:
        "Sagen Sie Ja zwischen Frangipani-Bäumen und Reisfeldern: standesamtliche Hochzeit, freie Trauung, Eheversprechen oder Jubiläum – wir planen Ihren Tag.",
    },
    en: {
      title: "Weddings in a Tropical Garden in Bali | Bali VW Tour",
      description:
        "Say 'I do' among frangipani trees and rice fields: civil wedding, symbolic ceremony, vow renewal or anniversary – we plan your day in Mengwi, Bali.",
    },
  },
  "/weddings/general-wedding-information": {
    de: {
      title: "Hochzeit auf Bali: alle Infos im Überblick | Bali VW Tour",
      description:
        "Alle wichtigen Infos zur Hochzeit auf Bali: Ablauf, benötigte Dokumente, Legalität und was eine standesamtliche Trauung in Indonesien rechtsgültig macht.",
    },
    en: {
      title: "Wedding in Bali: All the Key Information | Bali VW Tour",
      description:
        "All the key information on getting married in Bali: procedure, required documents, legality and what makes a civil wedding in Indonesia legally valid.",
    },
  },
  "/weddings/wedding-with-registry-office": {
    de: {
      title: "Standesamtlich heiraten auf Bali | Bali VW Tour",
      description:
        "Rechtlich gültig heiraten auf Bali: standesamtliche Trauung im tropischen Garten, inklusive Dokumentenberatung, Zeremonie und Blumendekoration.",
    },
    en: {
      title: "Civil Wedding in Bali with Registry Office | Bali VW Tour",
      description:
        "Get legally married in Bali: a civil ceremony in our tropical garden including document support, the ceremony itself and floral decoration for your day.",
    },
  },
  "/weddings/wedding-without-registry-office": {
    de: {
      title: "Freie Trauung ohne Standesamt auf Bali | Bali VW Tour",
      description:
        "Symbolische Hochzeit ohne Standesamt: eine freie Trauung im tropischen Garten mit Zeremonie, Blumen und Dekoration – ganz nach Ihren persönlichen Wünschen.",
    },
    en: {
      title: "Symbolic Wedding without Registry Office | Bali VW Tour",
      description:
        "A symbolic wedding without the registry office: a free ceremony in our tropical garden with flowers and decoration, arranged entirely around your personal wishes.",
    },
  },
  "/weddings/vow-renewal": {
    de: {
      title: "Eheversprechen in Bali erneuern | Bali VW Tour",
      description:
        "Erneuern Sie Ihr Eheversprechen in den Reisfeldern Balis: romantische Zeremonie im tropischen Garten mit Blumenarrangement und persönlicher Betreuung.",
    },
    en: {
      title: "Vow Renewal Ceremony in Bali | Bali VW Tour",
      description:
        "Renew your vows in the rice fields of Bali: a romantic ceremony in the tropical garden with floral arrangements and personal care from our local team.",
    },
  },
  "/weddings/wedding-anniversary": {
    de: {
      title: "Hochzeitsjubiläum auf Bali feiern | Bali VW Tour",
      description:
        "Feiern Sie Ihr Hochzeitsjubiläum auf Bali: eine persönliche Zeremonie mit Blumendekoration im tropischen Garten, umgeben von Reisfeldern und Frangipanis.",
    },
    en: {
      title: "Celebrate Your Wedding Anniversary in Bali | Bali VW Tour",
      description:
        "Celebrate your wedding anniversary in Bali: a personal ceremony with floral decoration in the tropical garden, surrounded by rice fields and frangipani trees.",
    },
  },
  "/wedding-inquiry": {
    de: {
      title: "Hochzeit anfragen – Antwort in 24 Stunden | Bali VW Tour",
      description:
        "Erzählen Sie uns von Ihrer Traumhochzeit auf Bali. Wir antworten innerhalb von 24 Stunden persönlich per E-Mail und planen den nächsten Schritt mit Ihnen.",
    },
    en: {
      title: "Wedding Enquiry – Reply within 24 Hours | Bali VW Tour",
      description:
        "Tell us about your dream wedding in Bali. We reply personally by email within 24 hours and plan the next steps together with you and your partner.",
    },
  },
  "/activities": {
    de: {
      title: "Touren & Aktivitäten im echten Bali | Bali VW Tour",
      description:
        "Entdecken Sie das ursprüngliche Bali abseits der Touristenpfade: Taman Ayun, Reisterrassen, Tempel und Zeremonien – mit über 25 Jahren Erfahrung vor Ort.",
    },
    en: {
      title: "Tours & Activities in the Real Bali | Bali VW Tour",
      description:
        "Discover the original Bali away from the tourist trails: Taman Ayun, rice terraces, temples and ceremonies – backed by more than 25 years of local experience.",
    },
  },
  "/tour-info": {
    de: {
      title: "Allgemeine Infos zu den Ausflügen | Bali VW Tour",
      description:
        "Alles zu unseren Ausflügen auf Bali: Ablauf, Fahrzeuge und Reiseleiter – mit erfahrenen Guides, die die Insel wirklich kennen.",
    },
    en: {
      title: "General Information About Our Tours | Bali VW Tour",
      description:
        "Everything about our tours in Bali: how a day works, our vehicles and guides – led by people who truly know the island.",
    },
  },
  "/tour": {
    de: {
      title: "Tour-Details | Bali VW Tour",
      description:
        "Details zu unseren Touren auf Bali: Reiseverlauf, Höhepunkte, enthaltene Leistungen und die direkte Buchung bei Bali VW Tour in Mengwi.",
    },
    en: {
      title: "Tour Details | Bali VW Tour",
      description:
        "Details on our tours in Bali: itinerary, highlights, included services and direct booking with Bali VW Tour in Mengwi, surrounded by rice fields.",
    },
  },
  "/tour/adventure-04": {
    de: {
      title: "Tauchen & Kultur in Nordbali | Bali VW Tour",
      description:
        "Tauchen und Kultur kombiniert: Korallengärten im Norden Balis und die grossen Höhepunkte der Insel – ein unvergessliches Erlebnis mit erfahrenen Guides.",
    },
    en: {
      title: "Diving & Culture in North Bali | Bali VW Tour",
      description:
        "Diving and culture combined: coral gardens in North Bali and the island's greatest highlights – an unforgettable experience with our experienced local guides.",
    },
  },
  "/tour/day-trip-1": {
    de: {
      title: "Tagesausflug: die klassische Bali-Tour | Bali VW Tour",
      description:
        "Tanah Lot, der Pura Luhur im Batu-Karu-Regenwald, Reisterrassen, Bedugul und der Bratan-See – der klassische Bali-Tagesausflug mit deutschsprachigem Guide.",
    },
    en: {
      title: "Day Trip: The Classic Bali Tour | Bali VW Tour",
      description:
        "Tanah Lot, Pura Luhur in the Batu Karu rainforest, rice terraces, Bedugul and Lake Bratan – the classic Bali day trip with an English-speaking guide.",
    },
  },
  "/tour/day-trip-2": {
    de: {
      title: "Tagesausflug: das versteckte Bali | Bali VW Tour",
      description:
        "Taman Ayun, ein originaler Dorfmarkt, das Ayungtal und eine Wanderung durch den Regenwald – Bali abseits der Touristenpfade entdecken.",
    },
    en: {
      title: "Day Trip: The Hidden Bali | Bali VW Tour",
      description:
        "Taman Ayun, an original village market, the Ayung valley and a walk through the rainforest – discover the side of Bali that most visitors never get to see.",
    },
  },
  "/tour/day-trip-3": {
    de: {
      title: "Tagesausflug: Wanderung durch Reisfelder | Bali VW Tour",
      description:
        "Wanderung durch die Reisfelder Balis mit frischer Kokosnuss, Picknick-Mittagessen und einem Spaziergang am schwarzen Sandstrand im Westen der Insel.",
    },
    en: {
      title: "Day Trip: Rice Field Walk | Bali VW Tour",
      description:
        "A walk through the rice fields of Bali with a fresh coconut, a picnic lunch and a stroll along the black sand beach on the west coast of the island.",
    },
  },
  "/tour/day-trip-4": {
    de: {
      title: "Tagesausflug: Ost-Bali mit Goa Gajah | Bali VW Tour",
      description:
        "Goa Gajah, Ikat-Weberei in Gianyar, der Kehen-Tempel von Bangli und die Gerichtshalle Kerta Gosa in Klungkung – Kultur pur im Osten Balis.",
    },
    en: {
      title: "Day Trip: East Bali with Goa Gajah | Bali VW Tour",
      description:
        "Goa Gajah, ikat weaving in Gianyar, Kehen Temple in Bangli and the Kerta Gosa court of justice in Klungkung – a full day of culture in East Bali.",
    },
  },
  "/tour/day-trip-5": {
    de: {
      title: "Tagesausflug: Sonnenaufgang am Batur | Bali VW Tour",
      description:
        "Frühmorgendlicher Aufstieg zum 1717 Meter hohen Batur-Vulkan mit Sonnenaufgang und Fernblick bis zum Gunung Rinjani auf Lombok.",
    },
    en: {
      title: "Day Trip: Batur Volcano Sunrise Climb | Bali VW Tour",
      description:
        "An early-morning climb of the 1,717 metre Batur volcano with sunrise and views as far as Mount Rinjani on the neighbouring island of Lombok.",
    },
  },
  "/tour/day-trip-6": {
    de: {
      title: "Tagesausflug: Natur im Westen Balis | Bali VW Tour",
      description:
        "Töpferdorf Pejaten, Reisterrassen, der Munduk-Wasserfall und die Kraterseen Buyan und Tamblingan – die grüne, stille Seite von West-Bali.",
    },
    en: {
      title: "Day Trip: Nature in West Bali | Bali VW Tour",
      description:
        "The pottery village of Pejaten, rice terraces, Munduk waterfall and the crater lakes Buyan and Tamblingan – the green, quiet side of West Bali.",
    },
  },
  "/tour/day-trip-7": {
    de: {
      title: "Tagesausflug: Balis Künstler & Tempel | Bali VW Tour",
      description:
        "Barong-Tanz, die Steinmetze von Batubulan, Silberschmiede in Celuk, die Felsengräber von Gunung Kawi und die heiligen Quellen von Sebatu.",
    },
    en: {
      title: "Day Trip: Bali's Artists & Temples | Bali VW Tour",
      description:
        "Barong dance, the stone carvers of Batubulan, silversmiths in Celuk, the rock tombs of Gunung Kawi and the holy springs of Sebatu – arts and temples.",
    },
  },
  "/tour/multi-day-8": {
    de: {
      title: "Rundreise 8: Natur & Künstler, 2 Tage | Bali VW Tour",
      description:
        "Zweitägige Rundreise: Westbali mit Munduk-Wasserfall und Kraterseen, Übernachtung in unserer Villa und Balis Künstlerdörfer rund um Ubud.",
    },
    en: {
      title: "Tour 8: Nature & Artists, 2 Days | Bali VW Tour",
      description:
        "Two-day round trip: West Bali with Munduk waterfall and crater lakes, one night at our villa and the artists' villages around Ubud in central Bali.",
    },
  },
  "/tour/multi-day-9": {
    de: {
      title: "Rundreise 9: Impressionen Balis, 3 Tage | Bali VW Tour",
      description:
        "Dreitägige Rundreise mit Tanah Lot, Jatiluwih, dem versteckten Bali und Ubud – inklusive Übernachtungen in unserer Villa in Mengwi.",
    },
    en: {
      title: "Tour 9: Impressions of Bali, 3 Days | Bali VW Tour",
      description:
        "Three-day round trip with Tanah Lot, Jatiluwih, the hidden Bali and Ubud – including overnight stays at our villa in Mengwi surrounded by rice fields.",
    },
  },
  "/tour/multi-day-10": {
    de: {
      title: "Rundreise 10: Klassisches Bali, 4 Tage | Bali VW Tour",
      description:
        "Viertägige Rundreise zu Dörfern, Reisterrassen, Tempeln und Vulkanen – mit deutschsprachigem Reiseleiter und Übernachtungen unterwegs.",
    },
    en: {
      title: "Tour 10: Classic Bali, 4 Days | Bali VW Tour",
      description:
        "A four-day round trip through villages, rice terraces, temples and volcanoes – with an English-speaking guide and overnight stays along the route.",
    },
  },
  "/tour/multi-day-11": {
    de: {
      title: "Rundreise 11: mit Batur-Vulkan, 4 Tage | Bali VW Tour",
      description:
        "Klassische Bali-Rundreise mit Besteigung des Batur-Vulkans und Sonnenaufgang über den Vulkanbergen – vier Tage Kultur, Natur und Ausblicke.",
    },
    en: {
      title: "Tour 11: with Batur Volcano, 4 Days | Bali VW Tour",
      description:
        "The classic Bali round trip with a climb of the Batur volcano and sunrise over the volcanic mountains – four days of culture, nature and wide views.",
    },
  },
  "/tour/multi-day-12": {
    de: {
      title: "Rundreise 12: Kulturprogramm, 5 Tage | Bali VW Tour",
      description:
        "Fünftägige Bali-Rundreise mit mehr Zeit in und um Ubud sowie den wichtigsten Sehenswürdigkeiten der Insel – entspanntes Tempo, tiefe Einblicke.",
    },
    en: {
      title: "Tour 12: Culture Programme, 5 Days | Bali VW Tour",
      description:
        "A five-day Bali round trip with more time in and around Ubud plus the island's most important sights – a relaxed pace and deeper cultural insight.",
    },
  },
  "/tour/adventure-01": {
    de: {
      title: "Rafting auf dem Ayung-Fluss | Bali VW Tour",
      description:
        "Wildwasser-Rafting durch das tropische Ayungtal mit Wasserfällen, Dörfern und Reisterrassen – inklusive Ausrüstung, Transfer und Buffet.",
    },
    en: {
      title: "Ayung River Rafting in Bali | Bali VW Tour",
      description:
        "White-water rafting through the tropical Ayung valley with waterfalls, villages and rice terraces – equipment, transfer and buffet included in the price.",
    },
  },
  "/tour/adventure-02": {
    de: {
      title: "Segeln zur Insel Lembongan | Bali VW Tour",
      description:
        "Segeltörn mit dem Katamaran zur Insel Lembongan: Schnorcheln, Strandzeit, BBQ-Buffet und die Rückfahrt im Sonnenuntergang über der Bali-See.",
    },
    en: {
      title: "Sailing to Lembongan Island | Bali VW Tour",
      description:
        "A catamaran cruise to Lembongan Island: snorkelling, time on the beach, a BBQ buffet and the return sail into the sunset over the Bali Sea.",
    },
  },
  "/tour/adventure-03": {
    de: {
      title: "Tauchen in Nordbali abseits der Massen | Bali VW Tour",
      description:
        "Tauchen abseits des Massentourismus an den Riffen Nord-Balis – begleitet vom North Bali Dive Center, für Anfänger und erfahrene Taucher.",
    },
    en: {
      title: "Diving in North Bali | Bali VW Tour",
      description:
        "Diving away from mass tourism at the reefs of North Bali, guided by the North Bali Dive Center – suitable for beginners and experienced divers alike.",
    },
  },
  "/day-trips": {
    de: {
      title: "Tagesausflüge auf Bali ab Villa | Bali VW Tour",
      description:
        "Die interessantesten Plätze Balis in attraktiven Tagesausflügen: Tanah Lot, Reisterrassen, Batur-Vulkan und mehr – mit deutschsprachigem Reiseleiter.",
    },
    en: {
      title: "Bali Day Trips from Your Villa | Bali VW Tour",
      description:
        "The most interesting places in Bali on attractive day trips: Tanah Lot, rice terraces, Batur volcano and more – with an English-speaking private guide.",
    },
  },
  "/multi-day-tours": {
    de: {
      title: "Bali-Rundreisen mit privatem Guide | Bali VW Tour",
      description:
        "Erleben Sie Bali auf mehrtägigen Rundreisen von seinen schönsten Seiten – Kultur, Natur, persönliche Betreuung und Übernachtungen in unseren Villen.",
    },
    en: {
      title: "Bali Multi-Day Tours with Private Guide | Bali VW Tour",
      description:
        "Experience Bali on multi-day round trips from its most beautiful sides – culture, nature, personal care and overnight stays in our own private villas.",
    },
  },
  "/adventure-tours": {
    de: {
      title: "Erlebnistouren: Rafting, Segeln, Tauchen | Bali VW Tour",
      description:
        "Rafting auf dem Ayung-Fluss, Segeln nach Lembongan und Tauchen im Norden Balis – Aktivurlaub mit Spaß, Abenteuer und guter Organisation.",
    },
    en: {
      title: "Adventure Tours: Rafting, Sailing, Diving | Bali VW Tour",
      description:
        "Rafting on the Ayung River, sailing to Lembongan and diving in North Bali – an active holiday with fun, adventure and well-organised transfers.",
    },
  },
  "/bali-info/general": {
    de: {
      title: "Bali-Infos von A–Z für Ihre Reise | Bali VW Tour",
      description:
        "Praktische Infos für Ihre Bali-Reise von A bis Z: Einreise, Visum, Zoll, Geld, Gesundheit, Strom, Trinkgeld und vieles mehr – übersichtlich zusammengestellt.",
    },
    en: {
      title: "Bali Info from A–Z for Your Trip | Bali VW Tour",
      description:
        "Practical information for your Bali trip from A to Z: entry, visa, customs, money, health, electricity, tipping and much more – clearly compiled for you.",
    },
  },
  "/bali-info/about-bali": {
    de: {
      title: "Bali: Geschichte, Kultur und Reisanbau | Bali VW Tour",
      description:
        "Mehr über Bali erfahren: Land und Leute, die bewegte Geschichte der Insel und der Reisanbau als Herzstück des balinesischen Alltags.",
    },
    en: {
      title: "Bali: History, Culture and Rice Farming | Bali VW Tour",
      description:
        "Learn more about Bali: its people, the eventful history of the island and rice farming as the beating heart of everyday life in Balinese villages.",
    },
  },
  "/bali-info/books": {
    de: {
      title: "Bücher über Bali: unsere Lesetipps | Bali VW Tour",
      description:
        "Unsere Lesetipps über Bali: Romane, Reiseberichte und Sachbücher, die Land, Kultur und Geschichte der Insel näherbringen – persönlich ausgewählt.",
    },
    en: {
      title: "Books about Bali: Our Reading Tips | Bali VW Tour",
      description:
        "Our reading recommendations about Bali: novels, travelogues and non-fiction that bring the island's culture and history closer – personally selected.",
    },
  },
  "/bali-info/plants": {
    de: {
      title: "Pflanzenwelt Balis: Palmen & Blüten | Bali VW Tour",
      description:
        "Die Pflanzenwelt Balis entdecken: Nutzpflanzen, Palmen, Blüten und Wasserpflanzen mit Namen und Verwendungszweck – kompakt erklärt für Ihre Reise.",
    },
    en: {
      title: "Plants of Bali: Palms & Flowers | Bali VW Tour",
      description:
        "Discover the plants of Bali: useful plants, palms, flowers and water plants with their names and traditional uses – a compact guide for your trip.",
    },
  },
  "/bali-info/fruits": {
    de: {
      title: "Früchte Balis: Mango, Rambutan & Co. | Bali VW Tour",
      description:
        "Das Früchteangebot Balis ist über das ganze Jahr reich: Mango, Rambutan, Mangostan, Papaya und viele mehr – mit Saisonzeiten und Tipps zum Probieren.",
    },
    en: {
      title: "Fruits of Bali: Mango, Rambutan & More | Bali VW Tour",
      description:
        "Bali's fruit selection is rich all year round: mango, rambutan, mangosteen, papaya and many more – with seasons and tips for tasting them on the island.",
    },
  },
  "/bali-info/spices": {
    de: {
      title: "Gewürze Balis: Vanille, Zimt & Nelken | Bali VW Tour",
      description:
        "Gewürze und Genussmittel Balis: Vanille, Zimt, Nelken, Muskat, Ingwer und Kurkuma – wo sie wachsen und wie sie die balinesische Küche prägen.",
    },
    en: {
      title: "Spices of Bali: Vanilla, Cinnamon & Cloves | Bali VW Tour",
      description:
        "Bali's spices and flavourings: vanilla, cinnamon, cloves, nutmeg, ginger and turmeric – where they grow and how they shape Balinese cooking.",
    },
  },
  "/bali-info/rice": {
    de: {
      title: "Reisanbau auf Bali: Subak & Reisfelder | Bali VW Tour",
      description:
        "Reisanbau auf Bali verstehen: Subak-Wassersystem, Reisterrassen und der Rhythmus der Ernte – Hintergründe zur Kulturlandschaft der Insel.",
    },
    en: {
      title: "Rice Farming in Bali: Subak & Terraces | Bali VW Tour",
      description:
        "Understand rice farming in Bali: the Subak irrigation system, the terraces and the rhythm of the harvest – background on the island's cultural landscape.",
    },
  },
  "/bali-info": {
    de: {
      title: "Bali-Info: Tipps vor Ihrer Reise | Bali VW Tour",
      description:
        "Alles, was Sie vor Ihrer Bali-Reise wissen sollten: praktische Tipps, Geschichte, Reisanbau, Feiertagskalender und persönliche Buchempfehlungen.",
    },
    en: {
      title: "Bali Info: Know Before You Go | Bali VW Tour",
      description:
        "Everything you should know before your Bali trip: practical tips, history, rice cultivation, the holiday calendar and personal book recommendations.",
    },
  },
  "/voucher": {
    de: {
      title: "Ihre Buchungsbestätigung | Bali VW Tour",
      description:
        "Ihre persönliche Buchungsbestätigung mit allen Details zu Aufenthalt, Tour und Kontaktdaten – zum Ansehen und Ausdrucken.",
    },
    en: {
      title: "Your Booking Confirmation | Bali VW Tour",
      description:
        "Your personal booking confirmation with all details on your stay, tour and contact information – ready to view and print for your trip to Bali.",
    },
  },
  "/shopping-bali": {
    de: {
      title: "Shoppen auf Bali: Märkte & Boutiquen | Bali VW Tour",
      description:
        "Wo man auf Bali am besten einkauft: Kunsthandwerk, Ikat-Stoffe, Silberschmuck und lokale Märkte – unsere persönlichen Tipps für Ihre Shoppingtour.",
    },
    en: {
      title: "Shopping in Bali: Markets & Boutiques | Bali VW Tour",
      description:
        "Where to shop best in Bali: handicrafts, ikat fabrics, silver jewellery and local markets – our personal tips for your shopping trip around the island.",
    },
  },
  "/health-sports": {
    de: {
      title: "Wellness, Yoga & Sport auf Bali | Bali VW Tour",
      description:
        "Yoga, Massage, Wellness und sportliche Aktivitäten rund um unsere Villen: für Erholung, neue Energie und einen aktiven Urlaub auf Bali.",
    },
    en: {
      title: "Wellness, Yoga & Sports in Bali | Bali VW Tour",
      description:
        "Yoga, massage, wellness and sports around our villas in Mengwi: for relaxation, new energy and an active holiday on the island of Bali.",
    },
  },
  "/contact": {
    de: {
      title: "Kontakt für Reservierung & Hochzeit | Bali VW Tour",
      description:
        "Kontaktieren Sie uns für Reservierungen, Touren, Hochzeitsplanung oder Fragen. Wir sprechen Deutsch, Englisch und Bahasa Indonesia.",
    },
    en: {
      title: "Contact for Reservations & Weddings | Bali VW Tour",
      description:
        "Contact us for reservations, tours, wedding planning or any questions. We speak English, German and Bahasa Indonesia and reply personally.",
    },
  },
  "/impressum": {
    de: {
      title: "Impressum & AGB | Bali VW Tour",
      description:
        "Impressum, Haftungsausschluss und Allgemeine Geschäftsbedingungen der Bali VW Tour mit allen Angaben zum Anbieter und zur Buchung.",
    },
    en: {
      title: "Legal Notice & Terms | Bali VW Tour",
      description:
        "Legal notice, disclaimer and general terms and conditions of Bali VW Tour, including all details about the provider and how bookings are handled.",
    },
  },
  "/privacy": {
    de: {
      title: "Datenschutzerklärung | Bali VW Tour",
      description:
        "Datenschutzerklärung der Bali VW Tour: Informationen zum Schutz Ihrer Daten, zu Cookies und zu Ihren Rechten als Besucher unserer Website.",
    },
    en: {
      title: "Privacy Policy | Bali VW Tour",
      description:
        "Privacy policy of Bali VW Tour: information on the protection of your data, on cookies and on your rights as a visitor of our website.",
    },
  },
};